import React, { useEffect, useState } from "react";
import SuperUserHeader from "../../components/SuperUserHeader";
import SuperUserSideMenu from "../../components/SuperUserSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import TextInput from "../../components/TextInput";
import CustomTextarea from "../../components/CustomTextarea";
import PasswordInput from "../../components/PasswordInput";
import ConfirmPopup from "../../components/ConfirmPopup";
import CustomButton from "../../components/CustomButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import { LoadingSpinner } from "../../components/Icons";
import { validatePassword, validateFieldReq, validateEmail } from "../../utils/validation";
import { formatName, formatIdWithLength } from "../../utils/format";
import {
    getSuperUserDetail,
    setSuccess,
    setError,
    updateSuperUserDetail,
    deleteSuperUserDetail,
} from "../../reducers/superUser";
import ReturnButton from "../../components/ReturnButton";
import NotFoundPage from "../../pages/NotFoundPage";

const initialData = {
    id: "",
    first_name: "",
    last_name: "",
    email: "",
    note: "",    
};

const initialErrorData = {
    first_name: "",
    last_name: "",
    email: "",
    note: "",    
};

const UserEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const {
        loading,
        success,
        error,
        actionName,
        actionStatus,
        detailData,
    } = useSelector((state: any) => state.superUser);
    const { id } = useParams<{ id: string }>();
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState(initialErrorData);
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState<any>(null);

    useEffect(() => {
        if(id){
            const data = {
                id: Number(id),
            }
            dispatch(getSuperUserDetail(data));
        }
    }, [id]);

    useEffect(() => {
        if(detailData.id){
            setFormData(detailData);
        }
    }, [detailData]);

    const userName = formatName(detailData.first_name, detailData.last_name);

    const validateFormData = () => {
        let formErrors = {
            first_name: "",
            last_name: "",
            email: "",
            note: "", 
        };

        const validateLastnameRes = validateFieldReq(formData.last_name, "姓");
        if(validateLastnameRes != true) {
            formErrors.last_name = String(validateLastnameRes);
        }

        const validateFirstnameRes = validateFieldReq(formData.first_name, "名");
        if(validateFirstnameRes != true) {
            formErrors.first_name = String(validateFirstnameRes);
        }
        
        const validateEmailRes = validateEmail(formData.email);
        if(validateEmailRes != true) {
            formErrors.email = String(validateEmailRes);
        }

        setErrors(formErrors);
        return !Object.values(formErrors).some((error) => error);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (validateFormData()) {
            const data  = {
                id: detailData.id,
                requestBody: {
                    id: detailData.id,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    note: formData.note,
                },
            }
            dispatch(updateSuperUserDetail(data));
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDelete = () => {
        setConfirmText(
            <>
                <p>
                    {userName} を削除します。
                    <br />
                    よろしいですか？
                </p>
            </>
        );
        setOpen(true);
    }

    const handleConfirm = () => {
        const data = {
            id: detailData.id,
        }
        dispatch(deleteSuperUserDetail(data));
        setOpen(false);
    }

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    useEffect(() => {
        if(error) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success) {
            if(actionName == "deleteSuperUserDetail") {
                navigate("/control/user");
                return;
            }
            if(actionName == "updateSuperUserDetail") {
                const data = {
                    ...detailData,
                }
                setFormData(data);
            }
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={success}
                    variant={"success"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setSuccess(null));
        }
    }, [error, success, actionName]);

    if (!detailData || (Array.isArray(detailData) && detailData.length === 0) || Object.keys(detailData).length === 0) {
        if(actionName == "getSuperUserDetail" && actionStatus == "rejected") return <NotFoundPage />;
        return ;
    }

    return (
        <div className="bg-alu-base h-dvh">
        <SuperUserHeader />
        <div className="mt-6 px-6 flex gap-6 relative">
            <SuperUserSideMenu />
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <div className="mb-6 flex items-center gap-4">
                        <ReturnButton onClick={() => navigate(-1)} />
                        <span className="text-sm alu-text-base-1">/</span>
                        <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                            {userName + " の情報を編集"}
                        </h3>
                    </div>
                    <div className="mb-6">
                        <CustomButton
                            type="button"
                            variant="outline"
                            size="sm"
                            customStyle="!text-[#F33D62] !border-[#F33D62] hover:!bg-[#FFC7F0]"
                            onClick={handleDelete}
                        >
                            このスーパーユーザーを削除
                        </CustomButton>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                    <form className="w-full">
                        <div className="w-[50%] max-w-[464px]">
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    ID
                                </label>
                                <p className="text-sm alu-text-base-2">{formatIdWithLength(formData.id)}</p>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    名前 <span className="alu-text-error">*</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <div className="w-full">
                                        <TextInput
                                            name="last_name"
                                            placeholder="姓"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            error={errors.last_name}
                                            errorText={errors.last_name}
                                            required
                                        />
                                    </div>
                                    <div className="w-full">
                                        <TextInput
                                            name="first_name"
                                            placeholder="名"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            error={errors.first_name}
                                            errorText={errors.first_name}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    メールアドレス <span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full">
                                    <TextInput
                                        error={errors.email}
                                        errorText={errors.email}
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="入力してください"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <label className="block mb-2 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    備考
                                </label>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        name="note"
                                        placeholder="入力してください"
                                        value={formData.note}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="w-full mt-6 grid grid-cols-2 items-center gap-4">
                                <CustomButton
                                    type="button"
                                    size="md"
                                    width="full"
                                    variant="outline"
                                    onClick={() => navigate(-1)}
                                >
                                    戻る
                                </CustomButton>
                                <CustomButton
                                    type="button"
                                    size="md"
                                    width="full"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    <p>{loading ? "保存中..." : "更新"}</p>
                                    {loading && (
                                        <span><LoadingSpinner /></span>
                                    )}
                                </CustomButton>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        {open && confirmText && (
            <ConfirmPopup
                type="delete"
                open={open}
                onClose={() => setOpen(false)}
                confirmContent={confirmText}
                onConfirm={handleConfirm}
            />
        )}
        </div>
    );
};

export default UserEdit;
